//helpers
import { getUrl, ROUTES } from 'shared/api/urls';
import { ApiHelper } from 'shared/helpers/api';
import { getNewCartHeader } from 'shared/api/helpers';
import { getDeviceType } from 'shared/components/ResponsiveLoader/helpers';

//constants
import { RENDER_MODE } from 'shared/constants';

//defs
interface IGenerateOTPProps {
  mobileNumber: string;
  email: string;
  id: string;
}

interface ICheckIfUserExists {
  mobileNumber?: string;
  email?: string;
}

const getDeviceTypeValue = () => {
  return getDeviceType() === RENDER_MODE.MOBILE ? 'MSITE' : 'WEBSITE';
};

export const checkIfUserExists = ({
  mobileNumber = '',
  email = '',
}: ICheckIfUserExists): Promise<any> => {
  const body = { customerEmail: email, customerMobile: mobileNumber };

  return ApiHelper(getUrl(ROUTES.CHECK_USER_EXISTS), {
    method: 'POST',
    body,
    isBodyJSON: true,
  }).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    return data.json();
  });
};

export const generateOTP = ({
  mobileNumber,
  email,
  id,
}: Partial<IGenerateOTPProps>): Promise<any> => {
  const body = { customer_email: email, customer_mobile: mobileNumber, id };

  return ApiHelper(getUrl(ROUTES.GENERATE_OTP), {
    method: 'POST',
    body,
    isBodyJSON: true,
  }).then(async (data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    const json = await data.json();
    const { status, response } = json || {};
    const { otpId } = response || {};
    // Treat "success without otpId" as a transport-level failure so every
    // caller gets a uniform rejection path. Mirrors the equivalent guard
    // in the Grahak `sendOtp` adapter.
    if (status === 'success' && !otpId) {
      return Promise.reject(new Error(json?.message || 'Missing otpId in generateOTP response'));
    }
    return json;
  });
};

export const resendOTP = (otpId: string): Promise<any> => {
  const body = { otpId };

  return ApiHelper(getUrl(ROUTES.RESEND_OTP), {
    method: 'POST',
    body,
    isBodyJSON: true,
  }).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject({ statusCode: data.status });
    }
    return data.json();
  });
};

export const verifyMobile = (otpId: string, otpNum: string): Promise<any> => {
  const body = { otpId, otpNum };
  return ApiHelper(getUrl(ROUTES.LINK_MOBILE_TO_ACCOUNT), {
    method: 'POST',
    body,
    isBodyJSON: true,
    headers: getNewCartHeader(),
  }).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    return data.json();
  });
};

export const login = (
  otpId: string,
  otpNum: string,
  authToken: string,
  timeoutValue: number
): Promise<any> => {
  const body = { otpId, otpNum, platform: getDeviceTypeValue() };

  return ApiHelper(
    getUrl(ROUTES.LOGIN_USING_OTP),
    {
      method: 'POST',
      body,
      isBodyJSON: true,
      headers: getNewCartHeader(),
    },
    timeoutValue
  ).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    return data.json();
  });
};

export const linkEmailToAccount = (
  otpId: string,
  otpNum: string,
  authToken: string
): Promise<any> => {
  const body = { otpId, otpNum, authToken, platform: getDeviceTypeValue() };

  return ApiHelper(getUrl(ROUTES.LINK_EMAIL_TO_ACCOUNT), {
    method: 'POST',
    body,
    isBodyJSON: true,
    headers: getNewCartHeader(),
  }).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    return data.json();
  });
};

export const mergeAccounts = (otpId: string, otpNum: string): Promise<any> => {
  const body = { otpId, otpNum };

  return ApiHelper(getUrl(ROUTES.MERGE_ACCOUNT), {
    method: 'POST',
    body,
    isBodyJSON: true,
    headers: getNewCartHeader(),
  }).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    return data.json();
  });
};

export const registerNewEmailForCurrentMobile = (authToken: string): Promise<any> => {
  const body = { authToken, platform: getDeviceTypeValue() };
  const URL = ROUTES.CREATE_EMAIL_FOR_REGISTERED_MOBILE;

  return ApiHelper(getUrl(URL), {
    method: 'POST',
    body,
    isBodyJSON: true,
    headers: getNewCartHeader(),
  }).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    return data.json();
  });
};

export const getEmailsToLink = (): Promise<any> => {
  const URL = ROUTES.ASSOCIATED_EMAILS_WITH_ACCOUNT;

  return ApiHelper(getUrl(URL)).then((data: Response) => {
    if (data.status !== 200) {
      return Promise.reject(data.statusText);
    }
    return data.json();
  });
};
